import React from 'react';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { ExternalLink, LayoutSingleColumn, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import marketingCss from '../_marketingPage/marketingPage.module.css';
import css from './HowToSellPage.module.css';

// Photo paths under `public/static/images/how-to-sell/`. Drop in newer
// edits with the same filenames to swap the imagery without touching
// the component.
const PHOTO_HERO = '/static/images/how-to-sell/hero.png';
const PHOTO_REGISTER = '/static/images/how-to-sell/register.png';
const PHOTO_STEP_STYLING = '/static/images/how-to-sell/step-1-styling.png';
const PHOTO_PHOTOGRAPHY_GRID = '/static/images/how-to-sell/step-2-photography.webp';
const PHOTO_PACK_AND_SEND = '/static/images/how-to-sell/sold-pack-and-send.jpg';
const PHOTO_SIGNOFF = '/static/images/how-to-sell/signoff.jpg';

const ImageSlot = ({ src, alt, captionId, contained }) => {
  if (!src) {
    return null;
  }
  const imgClassName = contained ? css.figureImageContained : css.figureImage;
  return (
    <figure className={css.figure}>
      <img src={src} alt={alt} className={imgClassName} loading="lazy" />
      {captionId ? (
        <figcaption className={css.figureCaption}>
          <FormattedMessage id={captionId} />
        </figcaption>
      ) : null}
    </figure>
  );
};

const ListingStep = ({ index, headingId, bodyId, figure }) => (
  <div className={css.step}>
    <h3 className={css.stepHeading}>
      <span className={css.stepNumber}>{index}</span>
      <FormattedMessage id={headingId} />
    </h3>
    <p className={css.body}>
      <FormattedMessage id={bodyId} />
    </p>
    {figure}
  </div>
);

const StepCard = ({ codeId, headingId, bodyId }) => (
  <div className={css.stepCard}>
    <p className={css.stepCode}>
      <FormattedMessage id={codeId} />
    </p>
    <h3 className={css.stepCardHeading}>
      <FormattedMessage id={headingId} />
    </h3>
    <p className={css.stepCardBody}>
      <FormattedMessage id={bodyId} />
    </p>
  </div>
);

const SubBlock = ({ headingId, bodyId }) => (
  <div className={css.subBlock}>
    <h3 className={css.subBlockHeading}>
      <FormattedMessage id={headingId} />
    </h3>
    <p className={css.body}>
      <FormattedMessage id={bodyId} />
    </p>
  </div>
);

/**
 * "Slik selger du" page (`/how-to-sell`).
 *
 * Long-form seller onboarding page, replaces the old SellSafelyPage in
 * the footer. Editorial / magazine treatment per the May 2026 client
 * brief: hero + 3-step badge row + "Slik kommer du i gang" + six-step
 * listing walkthrough + trust section + responsibility note + sold-flow
 * + commission + tips + sign-off.
 *
 * Photography is wired but the slot constants up top are empty strings,
 * so the page renders text-only until the client drops the real images
 * into `public/static/images/how-to-sell/`.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const HowToSellPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;

  const supportEmail = 'post@zuprize.no';
  const emailLink = (
    <ExternalLink href={`mailto:${supportEmail}`} className={marketingCss.kontaktLink}>
      {supportEmail}
    </ExternalLink>
  );
  const strong = chunks => <strong>{chunks}</strong>;

  return (
    <Page
      title={intl.formatMessage({ id: 'HowToSellPage.metaTitle' })}
      description={intl.formatMessage({ id: 'HowToSellPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="HowToSellPage" />} footer={<FooterContainer />}>
        <article className={css.root}>
          <div className={css.content}>
            <header className={css.hero}>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="HowToSellPage.heroHeading" />
              </h1>
              <p className={css.heroBody}>
                <FormattedMessage id="HowToSellPage.heroBody" />
              </p>
              <ImageSlot src={PHOTO_HERO} alt="" />
            </header>

            <section className={css.stepsRow}>
              <StepCard
                codeId="HowToSellPage.badge1Code"
                headingId="HowToSellPage.badge1Heading"
                bodyId="HowToSellPage.badge1Body"
              />
              <StepCard
                codeId="HowToSellPage.badge2Code"
                headingId="HowToSellPage.badge2Heading"
                bodyId="HowToSellPage.badge2Body"
              />
              <StepCard
                codeId="HowToSellPage.badge3Code"
                headingId="HowToSellPage.badge3Heading"
                bodyId="HowToSellPage.badge3Body"
              />
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.startHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToSellPage.startBody" />
              </p>
              <ImageSlot src={PHOTO_REGISTER} alt="" />
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.perfectAdHeading" />
              </h2>
              <p className={css.sectionLede}>
                <FormattedMessage id="HowToSellPage.perfectAdLede" />
              </p>

              <ListingStep
                index="01"
                headingId="HowToSellPage.step1Heading"
                bodyId="HowToSellPage.step1Body"
                figure={<ImageSlot src={PHOTO_STEP_STYLING} alt="" />}
              />
              <ListingStep
                index="02"
                headingId="HowToSellPage.step2Heading"
                bodyId="HowToSellPage.step2Body"
                figure={<ImageSlot src={PHOTO_PHOTOGRAPHY_GRID} alt="" contained />}
              />
              <ListingStep
                index="03"
                headingId="HowToSellPage.step3Heading"
                bodyId="HowToSellPage.step3Body"
              />
              <ListingStep
                index="04"
                headingId="HowToSellPage.step4Heading"
                bodyId="HowToSellPage.step4Body"
              />
              <ListingStep
                index="05"
                headingId="HowToSellPage.step5Heading"
                bodyId="HowToSellPage.step5Body"
              />
              <ListingStep
                index="06"
                headingId="HowToSellPage.step6Heading"
                bodyId="HowToSellPage.step6Body"
              />
            </section>

            <blockquote className={css.pullQuote}>
              <FormattedMessage id="HowToSellPage.quote1" />
            </blockquote>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToSellPage.trustEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.trustHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToSellPage.trustLede" />
              </p>
              <SubBlock
                headingId="HowToSellPage.trustBlock1Heading"
                bodyId="HowToSellPage.trustBlock1Body"
              />
              <SubBlock
                headingId="HowToSellPage.trustBlock2Heading"
                bodyId="HowToSellPage.trustBlock2Body"
              />
              <SubBlock
                headingId="HowToSellPage.trustBlock3Heading"
                bodyId="HowToSellPage.trustBlock3Body"
              />
              <SubBlock
                headingId="HowToSellPage.trustBlock4Heading"
                bodyId="HowToSellPage.trustBlock4Body"
              />
            </section>

            <blockquote className={css.pullQuote}>
              <FormattedMessage id="HowToSellPage.quote2" />
            </blockquote>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToSellPage.responsibilityEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.responsibilityHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToSellPage.responsibilityBody" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.soldHeading" />
              </h2>
              <p className={css.sectionLede}>
                <FormattedMessage id="HowToSellPage.soldLede" values={{ em: chunks => <em>{chunks}</em> }} />
              </p>

              <ListingStep
                index="01"
                headingId="HowToSellPage.sold1Heading"
                bodyId="HowToSellPage.sold1Body"
                figure={
                  <ImageSlot
                    src={PHOTO_PACK_AND_SEND}
                    alt=""
                    captionId="HowToSellPage.sold1Caption"
                  />
                }
              />
              <ListingStep
                index="02"
                headingId="HowToSellPage.sold2Heading"
                bodyId="HowToSellPage.sold2Body"
              />
              <ListingStep
                index="03"
                headingId="HowToSellPage.sold3Heading"
                bodyId="HowToSellPage.sold3Body"
              />
            </section>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToSellPage.commissionEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.commissionHeading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="HowToSellPage.commissionBody" />
              </p>
            </section>

            <section className={css.section}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="HowToSellPage.tipsEyebrow" />
              </p>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="HowToSellPage.tipsHeading" />
              </h2>
              <p className={css.sectionLede}>
                <FormattedMessage id="HowToSellPage.tipsLede" />
              </p>
              <div className={css.tipsList}>
                <p className={css.tip}>
                  <FormattedMessage id="HowToSellPage.tip1" values={{ strong }} />
                </p>
                <p className={css.tip}>
                  <FormattedMessage id="HowToSellPage.tip2" values={{ strong }} />
                </p>
                <p className={css.tip}>
                  <FormattedMessage id="HowToSellPage.tip3" values={{ strong }} />
                </p>
                <p className={css.tip}>
                  <FormattedMessage id="HowToSellPage.tip4" values={{ strong }} />
                </p>
              </div>
            </section>

            <p className={css.signOff}>
              <FormattedMessage id="HowToSellPage.signOff" />
            </p>

            <ImageSlot src={PHOTO_SIGNOFF} alt="" captionId="HowToSellPage.signOffCaption" />
          </div>

          <section className={marketingCss.kontakt}>
            <div className={marketingCss.kontaktInner}>
              <p className={marketingCss.kontaktEyebrow}>
                <FormattedMessage id="HowToSellPage.kontaktEyebrow" />
              </p>
              <p className={marketingCss.kontaktBody}>
                <FormattedMessage
                  id="HowToSellPage.kontaktBody"
                  values={{ br: <br />, email: emailLink }}
                />
              </p>
            </div>
          </section>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HowToSellPage = connect(mapStateToProps)(HowToSellPageComponent);

export { HowToSellPageComponent };
export default HowToSellPage;
